import { Fragment } from "react";
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { t } from "../dashboard/coach/coachTokens";
import {
  formatAmount,
  formatNutrientUnit,
  getServingMicronutrients,
  resolveNutritionBasis,
} from "./foodDictionaryNutrientUtils";

const CATEGORY_LABEL = { VITAMIN: "ויטמינים", MINERAL: "מינרלים", OTHER: "אחר" };
const CATEGORY_ORDER = ["MACRO", "OTHER", "VITAMIN", "MINERAL"];

/**
 * @param servingId the portion the reader has selected in the servings panel. When it names a
 *                  portion this food has, the whole table is read from it — that is what makes
 *                  clicking a portion change these numbers. Absent or unknown, the table falls back
 *                  to the 100 g / default basis it has always used.
 */
const FoodDictionaryNutrientTable = ({ food, servingId = null }) => {
  const picked = (food.servings || []).find((s) => s.id === servingId);
  const basis = picked
    ? { serving: picked, basisLabel: `למנה: ${picked.name}` }
    : resolveNutritionBasis(food);
  const { serving, basisLabel } = basis;

  const nv = serving
    ? (serving.nutritionValues || serving)
    : (food.defaultServingNutrition || {});

  // A portion usually carries macros but not micronutrients — those are measured once, on whichever
  // portion the source published (typically 100 g). getServingMicronutrients scales from that anchor,
  // so switching to "פחית (330 מ״ל)" moves the vitamins with the calories instead of blanking them.
  const micros = serving
    ? getServingMicronutrients(food, serving)
    : (food.defaultServingMicronutrients || []);

  // Build a single flat list of rows, grouped by category, macros first.
  const rows = [
    { key: "calories", category: "MACRO", name: "קלוריות", amount: nv.calories, unit: "קל׳" },
    { key: "protein", category: "MACRO", name: "חלבון", amount: nv.protein, unit: "גר'" },
    { key: "carbs", category: "MACRO", name: "פחמימות", amount: nv.carbs, unit: "גר'" },
    { key: "fats", category: "MACRO", name: "שומן", amount: nv.fats, unit: "גר'" },
    ...micros.map((m) => ({
      key: m.code,
      category: m.category || "OTHER",
      name: m.nameHe || m.nameEn || m.code,
      amount: m.amount,
      unit: formatNutrientUnit(m.unit),
    })),
  ];

  const grouped = CATEGORY_ORDER
    .map((cat) => ({ cat, items: rows.filter((r) => r.category === cat) }))
    .filter((g) => g.items.length > 0);

  const cellHead = {
    fontSize: "11px", fontWeight: 700, color: t.t3, textTransform: "uppercase",
    letterSpacing: ".05em", borderBottom: `1px solid ${t.border}`, py: 1,
  };
  const cell = { fontSize: "13.5px", color: t.text, borderBottom: `1px solid ${t.borderLight}`, py: 1 };

  return (
    <Box sx={{ background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r, boxShadow: t.sh, overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, px: 2.5, py: 1.75, borderBottom: `1px solid ${t.border}` }}>
        <Typography sx={{ fontSize: "15px", fontWeight: 700, color: t.text }}>ערכים תזונתיים</Typography>
        {basisLabel && <Typography sx={{ fontSize: "12.5px", color: t.t2 }}>· {basisLabel}</Typography>}
      </Box>

      <Table size="small" sx={{ direction: "rtl" }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...cellHead, textAlign: "right" }}>רכיב</TableCell>
            <TableCell sx={{ ...cellHead, textAlign: "left", width: "30%" }}>כמות</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {grouped.map(({ cat, items }) => (
            <Fragment key={cat}>
              {cat !== "MACRO" && (
                <TableRow key={`h-${cat}`}>
                  <TableCell colSpan={2} sx={{ ...cell, textAlign: "right", background: t.bg, fontWeight: 700, color: t.t2, fontSize: "12px", py: 0.75 }}>
                    {CATEGORY_LABEL[cat] || cat}
                  </TableCell>
                </TableRow>
              )}
              {items.map((r) => (
                <TableRow key={r.key} hover>
                  <TableCell sx={{ ...cell, textAlign: "right", fontWeight: r.category === "MACRO" ? 600 : 400 }}>
                    {r.name}
                  </TableCell>
                  <TableCell sx={{ ...cell, textAlign: "left", fontVariantNumeric: "tabular-nums" }}>
                    {formatAmount(r.amount)} <Typography component="span" sx={{ fontSize: "11.5px", color: t.t3 }}>{r.unit}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default FoodDictionaryNutrientTable;
